// The phone companion: not the dashboard shrunk down, a separate app with
// four jobs you do standing up — see what's on, record a meeting, talk to
// your agent, deal with follow-ups. The deeper pages live under More.
//
// Sizing: the root is FIXED and sized from the visual viewport, and the tab
// bar sits on its bottom edge.
//
// Measured on an iOS 18.7 home-screen install (852pt screen, telemetry in
// diag.ts):
//   * with viewport-fit=cover the host gives us a webview that starts at
//     the physical top but is only 793pt tall, and draws its own opaque
//     strip in the 59pt below it (WebKit bug 301994's signature). No CSS
//     reaches that strip; index.html therefore does NOT request cover, so
//     iOS reserves the status bar at the top and the webview runs to the
//     bottom edge instead;
//   * without cover, env(safe-area-inset-*) is 0 everywhere, so the bar
//     carries its own reserve for the home indicator (`--nav-floor`, set
//     below from the screen size: 28px on home-indicator phones). If cover
//     ever comes back, the same expression honours the real inset;
//   * `100vh`/`100lvh` report the SCREEN (852) rather than the box we can
//     paint in, so nothing may be sized from them: innerHeight /
//     visualViewport is the only honest reading.
//
// The keyboard never hides the chat composer: the shell follows the visual
// viewport when a keyboard is up and the tab bar steps aside. Browser tabs
// keep the `100dvh` fallback because their toolbar animates the visual
// viewport and a pinned px height fights that.

import { useEffect, useState } from "react";
import { CalendarClock, ChevronLeft, Home, MessageSquare, Mic, MoreHorizontal } from "lucide-react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { TabCount } from "@/components/ui/Tabs";
import { FOCUS_RING } from "@/components/ui/controlStyles";
import { COUNT_DOT_TONE, countPillTone } from "@/features/attention/tones";
import { NotificationsBell } from "@/features/notifications/NotificationsBell";
import { humanizeSlug } from "@/lib/format";
import { isEmbedded } from "@/native/embedded";
import { useNativeHandoff } from "@/native/NativeLinkBridge";
import { useNativeRendered } from "@/native/renderReady";
import { useAuth } from "@/stores/auth";
import { cn } from "@/lib/utils";
import { useCompanionBackClaim } from "./backLink";
import { postPhoneDiag } from "./diag";
import { useCompanionAttention, useTabBadges, type TabBadge } from "./tabBadges";
import { useSignOut } from "@/features/meetings/recorder/SignOutGuard";
import { RecordingSlot } from "@/features/meetings/recorder/RecordingBar";
import { StartWorkHost } from "@/features/startwork/StartWorkHost";

const TABS = [
  { to: "/companion/today", label: "Today", icon: Home },
  { to: "/companion/record", label: "Record", icon: Mic },
  { to: "/companion/chat", label: "Chat", icon: MessageSquare },
  { to: "/companion/meetings", label: "Meetings", icon: CalendarClock },
  { to: "/companion/more", label: "More", icon: MoreHorizontal },
];

// Room under the icon row for the home indicator: `--nav-floor` (28px on
// home-indicator phones, 10px otherwise — env() reads 0 without
// viewport-fit=cover, see the note above) or most of a real bottom inset.
const NAV_RESERVE = "max(var(--nav-floor, 10px), calc(env(safe-area-inset-bottom, 0px) - 6px))";
// The fill continues this far PAST the shell's bottom edge. Clipped by the
// webview when the shell is sized right, and covers the strip when it is
// not — no arrangement of viewport lies can leave bare canvas under the bar.
const NAV_BLEED = "64px";

const BACK_LINK = cn(
  "-ml-2 inline-flex h-11 items-center gap-0.5 rounded-control pl-0.5 pr-2 text-base text-fg-muted [@media(hover:hover)]:hover:text-fg",
  FOCUS_RING,
);

const HOSTED = new Set(["projects", "deliverables", "skills", "subagents", "vault", "settings", "todos", "tasks", "workflows", "schedules", "apps", "memory"]);

// A child (role "family") gets the same shell, minus everything that is the
// operator's: no More (projects / deliverables / skills / vault / settings),
// no notifications bell — they have exactly one agent and the server
// refuses the rest anyway. This is presentation only; the
// authorization lives in deps.require_tenant / deps.Principal.
const CHILD_TABS = new Set(["/companion/today", "/companion/record", "/companion/chat", "/companion/meetings"]);
const TITLES: Record<string, string> = {
  today: "Today", record: "Record", chat: "Chat", meetings: "Meetings", more: "More",
  projects: "Projects", deliverables: "Deliverables", skills: "Skills", vault: "Vault", settings: "Settings",
  todos: "Todos", subagents: "Subagents", tasks: "Tasks", workflows: "Workflows", schedules: "Schedules", apps: "Apps",
  memory: "Memory",
};

/** Height/offset of the visual viewport as CSS vars + whether a keyboard is up. */
function useVisualViewport(): boolean {
  const [keyboard, setKeyboard] = useState(false);
  useEffect(() => {
    const vv = window.visualViewport;
    const root = document.documentElement;
    // iOS Safari home-screen flag; lib.dom's Navigator doesn't type it.
    const navWithFlag: Navigator & { standalone?: boolean } = navigator;
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches || navWithFlag.standalone === true;
    // Home-indicator reserve for the tab bar. Every iPhone/iPad without a
    // home button has a long edge of >= 812pt; a browser tab keeps its own
    // toolbar over that strip, so only a home-screen app reserves it.
    const notched = Math.max(screen.width, screen.height) >= 812;
    root.style.setProperty("--nav-floor", standalone && notched ? "28px" : "10px");
    let maxSeen = 0;
    let lastH = -1;
    let lastTop = -1;
    let lastKb: boolean | null = null;
    const apply = () => {
      const inner = window.innerHeight;
      const h = Math.round(vv ? vv.height : inner);
      const top = Math.round(vv ? vv.offsetTop : 0);
      if (h > maxSeen) maxSeen = h;
      // Keyboard up? Two signals, because the platforms differ:
      //  - iOS keeps the layout viewport full and shrinks only the visual one.
      //  - Android (interactive-widget=resizes-content) shrinks BOTH, so
      //    compare against the tallest viewport we have seen instead.
      const kb = inner - h > 120 || (maxSeen > 0 && h < maxSeen * 0.72);
      if (kb || standalone) {
        // ALWAYS pin in a home-screen app, from the VISUAL viewport: it is
        // the only reading that matches the box iOS actually lets us paint
        // in (see the note at the top of this file). A browser tab keeps
        // CSS ownership below instead.
        if (h !== lastH || top !== lastTop) {
          root.style.setProperty("--app-h", `${h}px`);
          root.style.setProperty("--app-top", `${top}px`);
          lastH = h;
          lastTop = top;
        }
      } else {
        root.style.removeProperty("--app-h");
        root.style.removeProperty("--app-top");
        lastH = -1;
        lastTop = -1;
      }
      if (kb !== lastKb) {
        // Keyboard just closed: iOS can leave the visual viewport shifted
        // by the amount it scrolled to reveal the field, which puts the
        // bottom of the shell below the screen. Put it back.
        if (lastKb === true && !kb) window.scrollTo(0, 0);
        lastKb = kb;
        setKeyboard(kb);
      }
    };
    apply();
    // The known iOS launch lie: a standalone PWA reports a short viewport
    // while it is launching and fires NO resize event when it converges, so
    // do not trust events alone — walk a settle ladder after mount.
    const raf = requestAnimationFrame(() => requestAnimationFrame(apply));
    const timers = [120, 350, 1000, 2500, 5000, 10000].map((ms) => window.setTimeout(apply, ms));
    vv?.addEventListener("resize", apply);
    vv?.addEventListener("scroll", apply);
    window.addEventListener("resize", apply);
    // Landscape is ~half the portrait height and trips the keyboard
    // heuristic unless the tallest-viewport baseline is relearned.
    const onOrientation = () => { maxSeen = 0; apply(); };
    window.addEventListener("orientationchange", onOrientation);
    const onVisibility = () => { if (document.visibilityState === "visible") apply(); };
    document.addEventListener("visibilitychange", onVisibility);
    const onPageShow = (e: PageTransitionEvent) => { if (e.persisted) apply(); };
    window.addEventListener("pageshow", onPageShow);
    document.body.classList.add("companion");
    return () => {
      vv?.removeEventListener("resize", apply);
      vv?.removeEventListener("scroll", apply);
      window.removeEventListener("resize", apply);
      window.removeEventListener("orientationchange", onOrientation);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
      cancelAnimationFrame(raf);
      timers.forEach((t) => window.clearTimeout(t));
      document.body.classList.remove("companion");
      root.style.removeProperty("--nav-floor");
      root.style.removeProperty("--app-h");
      root.style.removeProperty("--app-top");
    };
  }, []);
  return keyboard;
}

export function CompanionShell({ customerId }: { customerId: string }) {
  // Inside the iPhone app the app draws the frame. Decided once per page
  // (src/native/embedded.ts), so the branch never flips under a mounted shell.
  return isEmbedded() ? <EmbeddedShell /> : <PhoneShell customerId={customerId} />;
}

/**
 * A web screen inside the iPhone app (docs/ios-build-spec.md §7): the page
 * alone. The app owns the navigation bar, the tabs, the back control and
 * the bottom inset (`--app-inset-b` is 0, so a sheet or composer sits on
 * the web view's bottom edge). The keyboard path of useVisualViewport
 * stays, so a composer still clears the keyboard. No postPhoneDiag (it
 * acts only in a home-screen install anyway) and no tab-badge polling.
 */
function EmbeddedShell() {
  const location = useLocation();
  const isChild = useAuth((st) => st.role) === "family";
  const parts = location.pathname.split("/");
  const key = parts[2] ?? "today";
  const deep = parts.length > 3;
  useVisualViewport();
  // The app shows the page once it has drawn with its data, and its pull
  // to refresh refetches in place (src/native/renderReady.tsx).
  useNativeRendered();
  useEffect(() => {
    document.documentElement.style.setProperty("--app-inset-b", "0px");
    return () => {
      document.documentElement.style.removeProperty("--app-inset-b");
    };
  }, []);
  const ownScroll = key === "projects" || (key === "chat" && (deep || isChild));
  // A navigation to a screen the app draws (or to a Safari-only path) is
  // handed over and undone (NativeLinkBridge); render nothing meanwhile.
  const handingOff = useNativeHandoff(location);
  return (
    // Still a fixed frame: the keyboard path pins it to the visual viewport.
    <div className="fixed inset-x-0 flex flex-col text-fg" style={{ top: "var(--app-top, 0px)", height: "var(--app-h, 100dvh)" }}>
      <RecordingSlot stack />
      <main className={cn("relative z-0 min-h-0 flex-1", ownScroll ? "overflow-hidden" : "overflow-y-auto")}>
        {handingOff ? null : <Outlet />}
      </main>
      {/* "Give a task" over the screen, as a sheet: ?give=… (features/startwork). */}
      <StartWorkHost />
    </div>
  );
}

function PhoneShell({ customerId }: { customerId: string }) {
  // Home-screen installs phone their measurements home so layout/mic bugs
  // on one device read out in the server log, not over chat. No-op in a tab.
  useEffect(() => postPhoneDiag(), []);
  const isChild = useAuth((st) => st.role) === "family";
  const childAgent = useAuth((st) => st.agent);
  // Asks first while a meeting is being recorded (features/meetings/recorder/SignOutGuard.tsx).
  const { signOut, dialog: signOutDialog } = useSignOut();
  const tabs = isChild ? TABS.filter((t) => CHILD_TABS.has(t.to)) : TABS;
  const { pathname } = useLocation();
  const parts = pathname.split("/");
  const key = parts[2] ?? "today";
  const deep = parts.length > 3;                 // e.g. /companion/deliverables/<agent>/<id>
  const keyboard = useVisualViewport();
  // Screens that scroll inside themselves: a conversation (the thread,
  // the child's chat) and Projects. The agent list scrolls with the page.
  const ownScroll = key === "projects" || (key === "chat" && (deep || isChild));
  // A conversation takes the whole screen: its own header, no tab bar,
  // the composer on the bottom edge — the messenger convention.
  const thread = key === "chat" && deep;
  // What a bottom-edge control must clear: nothing under a keyboard, else
  // the home-indicator reserve (see --nav-floor).
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--app-inset-b",
      keyboard ? "0px" : "max(var(--nav-floor, 10px), env(safe-area-inset-bottom, 0px))",
    );
    return () => {
      document.documentElement.style.removeProperty("--app-inset-b");
    };
  }, [keyboard]);
  // An agent's memory is opened from its chat (/companion/memory/<agent>): back is that chat.
  const backTo = key === "memory" ? `/companion/chat/${parts[3] ?? ""}` : deep ? `/companion/${key}` : "/companion/more";
  const backLabel = key === "memory" ? "Chat" : deep ? (TITLES[key] ?? "Back") : "More";
  // A hosted page with a level of its own (an open project) takes the
  // back link over while it is open: one back control, not two.
  const claim = useCompanionBackClaim();
  const backClaim = HOSTED.has(key) ? claim : null;
  const badges = useTabBadges(useCompanionAttention(customerId), isChild);
  // A child has one agent: their chat is titled with its name.
  const title = isChild && key === "chat" && childAgent
    ? humanizeSlug(childAgent, null, { titleCase: true })
    : TITLES[key] ?? "Corvus";

  return (
    // Wider than a phone (a tablet, or a laptop window on /companion) the
    // app is a centred column; hairline edges keep it from floating.
    <div
      className="fixed left-0 right-0 mx-auto flex w-full max-w-[560px] flex-col text-fg min-[561px]:border-x min-[561px]:border-line-subtle"
      style={{ top: "var(--app-top, 0px)", height: "var(--app-h, 100dvh)", paddingTop: "env(safe-area-inset-top)" }}
    >
      {!thread && (
      <header className="flex h-12 shrink-0 items-center justify-between gap-2 px-4">
        {backClaim ? (
          // ui-exempt: the header's back link, run by the page (it pops history)
          <button type="button" onClick={backClaim.onBack} className={BACK_LINK}>
            <ChevronLeft aria-hidden className="h-5 w-5" /> {backClaim.label}
          </button>
        ) : HOSTED.has(key) ? (
          // The hosted page carries its own title (its h1, the same 24px
          // display face as the tab screens' below); the header only
          // offers the way back.
          <Link to={backTo} className={BACK_LINK}>
            <ChevronLeft aria-hidden className="h-5 w-5" /> {backLabel}
          </Link>
        ) : (
          // Every page title on the phone is the same 24px display h1 —
          // here in the bar, on a hosted page just under its back link.
          <h1 className="truncate font-display text-2xl tracking-[var(--font-display-tracking)] text-fg">{title}</h1>
        )}
        <div className="flex shrink-0 items-center gap-1">
          {isChild ? (
            // A child has no More tab and so no route to Settings — without
            // this there is no way for them to sign out at all, which
            // matters most on a shared family device.
            <>
              <Button variant="secondary" size="md" onClick={signOut}>
                Sign out
              </Button>
              {signOutDialog}
            </>
          ) : (
            // The bell stands alone: picking an agent happens in the Chat
            // list, where it is used.
            <NotificationsBell customerId={customerId} />
          )}
        </div>
      </header>
      )}
      {/* The recording (live, uploading, or asking "Carry on?") shows here: the page moves down for it. */}
      <RecordingSlot stack />
      <main className={cn("relative z-0 min-h-0 flex-1", ownScroll ? "overflow-hidden" : "overflow-y-auto")} style={!keyboard && !thread ? { paddingBottom: `calc(3.25rem + ${NAV_RESERVE})` } : undefined}>
        <Outlet />
      </main>
      <StartWorkHost />
      {!keyboard && !thread && (
        <nav
          className="absolute inset-x-0 z-30"
          style={{
            // Hangs NAV_BLEED past the shell's bottom edge and pads the same
            // amount back, so the icon row sits NAV_RESERVE above the edge
            // while the fill runs off the bottom of the screen. The fill is
            // the theme's OPAQUE bar colour: anything see-through would
            // show the system strip under it (see the note at the top).
            bottom: `-${NAV_BLEED}`,
            paddingBottom: `calc(${NAV_BLEED} + ${NAV_RESERVE})`,
            background: "var(--c-tabbar-bg)",
            borderTop: "1px solid var(--c-border-subtle)",
          }}
          aria-label="Companion navigation"
          data-companion-nav=""
        >
          <div className="flex h-[3.25rem] items-stretch">
            {tabs.map((t) => (
              <NavLink key={t.to} to={t.to}
                className={({ isActive }) => cn(
                  "relative flex flex-1 flex-col items-center justify-center gap-0.5 text-xs font-medium focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus",
                  isActive ? "text-accent-fg" : "text-fg-muted",
                )}>
                {({ isActive }) => (
                  <>
                    {isActive && <span aria-hidden className="absolute top-0 h-[2px] w-8 rounded-pill bg-accent-fg" />}
                    {/* Fixed 24px icon box so every label sits on one baseline (Record's icon is larger). */}
                    <span aria-hidden className="relative grid h-6 place-items-center">
                      <t.icon className={cn("h-5 w-5", t.to.endsWith("/record") && "h-6 w-6")} strokeWidth={isActive ? 2 : 1.6} />
                      <TabBadgeMark badge={badges[t.to]} />
                    </span>
                    {t.label}
                    {badges[t.to] && <span className="sr-only">, {badges[t.to]?.label}</span>}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}

/**
 * A count (or a dot) on a tab icon's top-right corner; geometry never
 * moves. Both sit below the active tab's 2px top indicator (the icon box
 * starts 5px down the bar), and the dot sits beside the ellipsis glyph,
 * which is drawn in the middle band of its box. The count is the shared
 * TabCount in the badge's tone (the desktop sidebar's: grey unless a todo
 * is overdue), on an opaque plate so the icon under a tinted
 * tone never shows through.
 */
function TabBadgeMark({ badge }: { badge?: TabBadge }) {
  if (!badge) return null;
  if (badge.count === null) {
    return (
      <span
        className={cn(
          "absolute -right-2 top-1 h-2 w-2 rounded-full ring-2 ring-[color:var(--c-tabbar-bg)]",
          COUNT_DOT_TONE[badge.tone],
        )}
      />
    );
  }
  return (
    <span className="absolute left-[calc(50%+8px)] top-0 flex rounded-pill bg-[color:var(--c-tabbar-bg)] ring-2 ring-[color:var(--c-tabbar-bg)]">
      <TabCount
        count={badge.count > 99 ? "99+" : badge.count}
        tone={countPillTone(badge.tone)}
        className="h-[18px] min-w-[18px] px-1 leading-none"
      />
    </span>
  );
}
